import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Zap, RefreshCw, Wind, TreePine, Brain, Trophy, Sparkles, Save, RotateCcw, Plus, Trash2, Loader2, Wand2, ChevronDown, ChevronRight } from 'lucide-react';

const GAME_META = [
  { key: 'thought_defense', icon: Zap,      label: 'Thought Defense', desc: '60-second swipe game.' },
  { key: 'reframe_battle',  icon: RefreshCw, label: 'Reframe Battle',  desc: 'CBT reframe scenarios.' },
  { key: 'breath_rhythm',   icon: Wind,      label: 'Breath Rhythm',   desc: '4-7-8 breathing orb.' },
  { key: 'streak_tree',     icon: TreePine,  label: 'Mindset Tree',    desc: 'Streak visualization.' },
  { key: 'inner_battle',    icon: Brain,     label: 'Inner Battle',    desc: 'Brain alignment.' },
];

const SECTION_TABS = [
  { id: 'toggles',   label: 'On / Off',  icon: Zap },
  { id: 'bubbles',   label: 'Thought Bubbles', icon: Sparkles },
  { id: 'scenarios', label: 'Reframe Scenarios', icon: RefreshCw },
  { id: 'xp',        label: 'XP Rewards', icon: Trophy },
];

export default function GameManager() {
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState('toggles');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/games/config');
      setCfg(r.data);
    } catch (e) {
      toast.error('Failed to load games config.', { description: String(e?.response?.data?.detail || e.message) });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading || !cfg) {
    return <div className="py-12 text-center text-muted-kc" data-testid="gm-loading"><Loader2 className="inline animate-spin mr-2" size={16}/>Loading games admin…</div>;
  }

  const update = (patch) => setCfg((c) => ({ ...c, ...patch }));

  return (
    <div data-testid="game-manager">
      {/* Section tabs */}
      <div className="flex gap-1 mb-5 overflow-x-auto" data-testid="gm-section-tabs">
        {SECTION_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold rounded-full whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${tab === t.id ? 'bg-ink text-white' : 'text-muted-kc hover:bg-surface-2 border border-kc'}`}
              data-testid={`gm-tab-${t.id}`}
            >
              <Icon size={12}/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'toggles'   && <TogglesPanel   cfg={cfg} onSaved={load} setSaving={setSaving} saving={saving}/>}
      {tab === 'bubbles'   && <BubblesPanel   cfg={cfg} onSaved={load} update={update} />}
      {tab === 'scenarios' && <ScenariosPanel cfg={cfg} onSaved={load} update={update} />}
      {tab === 'xp'        && <XpRewardsPanel cfg={cfg} onSaved={load} />}
    </div>
  );
}

/* =================== TOGGLES =================== */
function TogglesPanel({ cfg, onSaved, setSaving, saving }) {
  const [toggles, setToggles] = useState(cfg.toggles || {});

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/games/toggles', { toggles });
      toast.success('Game toggles saved.');
      onSaved();
    } catch (e) {
      toast.error('Save failed.', { description: String(e?.response?.data?.detail || e.message) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="gm-toggles">
      <p className="text-sm text-muted-kc">Switch a game off to hide it from the public Games Hub. Existing player stats are preserved.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {GAME_META.map((g) => {
          const Icon = g.icon;
          const on = toggles[g.key] !== false;
          return (
            <div key={g.key} className="flex items-center justify-between p-4 border border-kc rounded-[var(--kc-radius-md)] bg-white" data-testid={`gm-toggle-${g.key}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-surface-2"><Icon size={18} className="text-[color:var(--kc-gold)]"/></div>
                <div>
                  <div className="font-display uppercase text-base leading-tight">{g.label}</div>
                  <div className="text-xs text-muted-kc">{g.desc}</div>
                </div>
              </div>
              <Switch
                checked={on}
                onCheckedChange={(v) => setToggles({ ...toggles, [g.key]: !!v })}
                data-testid={`gm-switch-${g.key}`}
              />
            </div>
          );
        })}
      </div>
      <div className="pt-2 flex justify-end">
        <button onClick={save} disabled={saving} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold disabled:opacity-50" data-testid="gm-toggles-save">
          {saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>} Save toggles
        </button>
      </div>
    </div>
  );
}

/* =================== THOUGHT BUBBLES =================== */
function BubblesPanel({ cfg, onSaved }) {
  const [negText, setNegText] = useState((cfg.thought_bubbles.negative || []).join('\n'));
  const [posText, setPosText] = useState((cfg.thought_bubbles.positive || []).join('\n'));
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(null);    // 'negative' | 'positive' | null
  const [aiVibeNeg, setAiVibeNeg] = useState('');
  const [aiVibePos, setAiVibePos] = useState('');
  const [aiCountNeg, setAiCountNeg] = useState(10);
  const [aiCountPos, setAiCountPos] = useState(10);

  const save = async () => {
    setSaving(true);
    try {
      const neg = negText.split('\n').map(s => s.trim()).filter(Boolean);
      const pos = posText.split('\n').map(s => s.trim()).filter(Boolean);
      const r = await api.put('/admin/games/thought-bubbles', { negative: neg, positive: pos });
      setNegText((r.data.negative || []).join('\n'));
      setPosText((r.data.positive || []).join('\n'));
      toast.success(`Saved · ${r.data.negative.length} negative · ${r.data.positive.length} positive`);
      onSaved();
    } catch (e) {
      toast.error('Save failed.', { description: String(e?.response?.data?.detail || e.message) });
    } finally {
      setSaving(false);
    }
  };

  const aiGenerate = async (type) => {
    setAiBusy(type);
    try {
      const body = {
        type,
        count: type === 'negative' ? aiCountNeg : aiCountPos,
        vibe: (type === 'negative' ? aiVibeNeg : aiVibePos).trim(),
        append: true,
      };
      const r = await api.post('/admin/games/ai/thought-bubbles', body);
      setNegText((r.data.negative || []).join('\n'));
      setPosText((r.data.positive || []).join('\n'));
      toast.success(`AI added ${r.data.added} ${type} thoughts.`);
      onSaved();
    } catch (e) {
      toast.error('AI generation failed.', { description: String(e?.response?.data?.detail || e.message) });
    } finally {
      setAiBusy(null);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset both lists to defaults? Your custom additions will be lost.')) return;
    try {
      await api.post('/admin/games/reset-defaults', { sections: ['thought_bubbles'] });
      toast.success('Reset to defaults.');
      onSaved();
      const r = await api.get('/admin/games/config');
      setNegText((r.data.thought_bubbles.negative || []).join('\n'));
      setPosText((r.data.thought_bubbles.positive || []).join('\n'));
    } catch (e) {
      toast.error('Reset failed.');
    }
  };

  const negCount = negText.split('\n').filter(s => s.trim()).length;
  const posCount = posText.split('\n').filter(s => s.trim()).length;

  return (
    <div className="space-y-4" data-testid="gm-bubbles">
      <p className="text-sm text-muted-kc">One thought per line. Negative thoughts appear in red, positive in gold during the game. Auto-dedupes on save (max 200 each).</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BubbleColumn
          title="Negative Thoughts"
          subtitle="The intrusive ones to defend against."
          color="text-[color:var(--kc-danger)]"
          accent="border-l-4 border-[color:var(--kc-danger)]"
          value={negText}
          onChange={setNegText}
          count={negCount}
          aiVibe={aiVibeNeg}
          setAiVibe={setAiVibeNeg}
          aiCount={aiCountNeg}
          setAiCount={setAiCountNeg}
          onAi={() => aiGenerate('negative')}
          busy={aiBusy === 'negative'}
          testidPrefix="neg"
        />
        <BubbleColumn
          title="Positive Thoughts"
          subtitle="The friends to let pass."
          color="text-[color:var(--kc-gold-2)]"
          accent="border-l-4 border-[color:var(--kc-gold)]"
          value={posText}
          onChange={setPosText}
          count={posCount}
          aiVibe={aiVibePos}
          setAiVibe={setAiVibePos}
          aiCount={aiCountPos}
          setAiCount={setAiCountPos}
          onAi={() => aiGenerate('positive')}
          busy={aiBusy === 'positive'}
          testidPrefix="pos"
        />
      </div>

      <div className="flex justify-between gap-3 pt-2">
        <button onClick={reset} className="h-10 px-4 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] text-sm text-muted-kc hover:bg-surface-2 border border-kc" data-testid="gm-bubbles-reset"><RotateCcw size={14}/> Reset to defaults</button>
        <button onClick={save} disabled={saving} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold disabled:opacity-50" data-testid="gm-bubbles-save">
          {saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>} Save thoughts
        </button>
      </div>
    </div>
  );
}

function BubbleColumn({ title, subtitle, color, accent, value, onChange, count, aiVibe, setAiVibe, aiCount, setAiCount, onAi, busy, testidPrefix }) {
  return (
    <div className={`bg-white rounded-[var(--kc-radius-md)] p-4 border border-kc ${accent}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className={`font-display uppercase text-lg leading-tight ${color}`}>{title}</div>
          <div className="text-xs text-muted-kc">{subtitle}</div>
        </div>
        <div className="text-xs text-muted-kc tabular-nums">{count} lines</div>
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-64 p-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white font-mono text-xs leading-relaxed focus:outline-none focus:border-[color:var(--kc-gold)]"
        placeholder="One thought per line…"
        data-testid={`gm-${testidPrefix}-textarea`}
      />
      <div className="mt-3 p-3 rounded-[var(--kc-radius-sm)] bg-surface-2 border border-kc">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[color:var(--kc-gold-2)] mb-2 inline-flex items-center gap-1"><Wand2 size={11}/> Generate with AI</div>
        <input
          type="text"
          value={aiVibe}
          onChange={(e) => setAiVibe(e.target.value)}
          placeholder="Optional vibe (e.g. 'work anxiety', 'self-worth')"
          className="w-full h-9 px-3 mb-2 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-xs focus:outline-none focus:border-[color:var(--kc-gold)]"
          data-testid={`gm-${testidPrefix}-ai-vibe`}
        />
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="1"
            max="30"
            value={aiCount}
            onChange={(e) => setAiCount(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))}
            className="w-16 h-9 px-2 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-xs text-center"
            data-testid={`gm-${testidPrefix}-ai-count`}
          />
          <button
            onClick={onAi}
            disabled={busy}
            className="flex-1 h-9 inline-flex items-center justify-center gap-1.5 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black text-xs font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-50"
            data-testid={`gm-${testidPrefix}-ai-go`}
          >
            {busy ? <><Loader2 size={12} className="animate-spin"/> Generating…</> : <><Sparkles size={12}/> Generate {aiCount}</>}
          </button>
        </div>
        <div className="text-[10px] text-muted-kc mt-1.5">Appends to list, deduped. Save changes after.</div>
      </div>
    </div>
  );
}

/* =================== REFRAME SCENARIOS =================== */
function ScenariosPanel({ cfg, onSaved }) {
  const [items, setItems] = useState(cfg.reframe_scenarios || []);
  const [saving, setSaving] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiCount, setAiCount] = useState(3);
  const [aiTheme, setAiTheme] = useState('');

  const updateItem = (id, patch) => setItems(items.map(it => it.id === id ? { ...it, ...patch } : it));
  const updateOpt = (id, idx, patch) => setItems(items.map(it => {
    if (it.id !== id) return it;
    const opts = [...(it.options || [])];
    opts[idx] = { ...opts[idx], ...patch };
    return { ...it, options: opts };
  }));
  const markCorrect = (id, idx) => updateItem(id, { options: (items.find(i => i.id === id)?.options || []).map((o, i) => ({ ...o, score: i === idx ? 1 : 0 })) });
  const removeOpt = (id, idx) => updateItem(id, { options: (items.find(i => i.id === id)?.options || []).filter((_, i) => i !== idx) });
  const addOpt = (id) => updateItem(id, { options: [...(items.find(i => i.id === id)?.options || []), { text: '', score: 0, feedback: '' }] });
  const deleteItem = (id) => {
    if (!window.confirm('Delete this scenario?')) return;
    setItems(items.filter(it => it.id !== id));
  };
  const addNew = () => {
    const newId = `r${Math.random().toString(36).slice(2, 10)}`;
    setItems([...items, {
      id: newId,
      trigger: '',
      thought: '',
      options: [
        { text: '', score: 1, feedback: '' },
        { text: '', score: 0, feedback: '' },
        { text: '', score: 0, feedback: '' },
        { text: '', score: 0, feedback: '' },
      ],
    }]);
    setOpenId(newId);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/games/reframe-scenarios', { items });
      setItems(r.data.items || []);
      toast.success(`Saved · ${r.data.items.length} scenarios`);
      onSaved();
    } catch (e) {
      toast.error('Save failed.', { description: String(e?.response?.data?.detail || e.message) });
    } finally {
      setSaving(false);
    }
  };

  const aiGenerate = async () => {
    setAiBusy(true);
    try {
      const r = await api.post('/admin/games/ai/reframe-scenarios', { count: aiCount, theme: aiTheme.trim(), append: true });
      setItems(r.data.items || []);
      toast.success(`AI added ${r.data.added} scenarios.`);
      onSaved();
    } catch (e) {
      toast.error('AI generation failed.', { description: String(e?.response?.data?.detail || e.message) });
    } finally {
      setAiBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset to default scenarios? All custom additions will be lost.')) return;
    try {
      await api.post('/admin/games/reset-defaults', { sections: ['reframe_scenarios'] });
      toast.success('Reset to defaults.');
      const r = await api.get('/admin/games/config');
      setItems(r.data.reframe_scenarios || []);
      onSaved();
    } catch (e) {
      toast.error('Reset failed.');
    }
  };

  return (
    <div className="space-y-4" data-testid="gm-scenarios">
      <p className="text-sm text-muted-kc">Each scenario has a trigger, a negative thought, and 4 response options. Exactly one option should be marked as the strong reframe (gold star). The others are common distortions.</p>

      <div className="bg-surface-2 border border-kc rounded-[var(--kc-radius-md)] p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[color:var(--kc-gold-2)] mb-2 inline-flex items-center gap-1"><Wand2 size={11}/> Generate with AI</div>
        <input
          type="text"
          value={aiTheme}
          onChange={(e) => setAiTheme(e.target.value)}
          placeholder="Optional theme (e.g. 'parenting struggles', 'social anxiety')"
          className="w-full h-9 px-3 mb-2 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-xs focus:outline-none focus:border-[color:var(--kc-gold)]"
          data-testid="gm-scenarios-ai-theme"
        />
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="1"
            max="5"
            value={aiCount}
            onChange={(e) => setAiCount(Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))}
            className="w-16 h-9 px-2 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-xs text-center"
            data-testid="gm-scenarios-ai-count"
          />
          <button
            onClick={aiGenerate}
            disabled={aiBusy}
            className="flex-1 h-9 inline-flex items-center justify-center gap-1.5 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black text-xs font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-50"
            data-testid="gm-scenarios-ai-go"
          >
            {aiBusy ? <><Loader2 size={12} className="animate-spin"/> Generating…</> : <><Sparkles size={12}/> Generate {aiCount}</>}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {items.map((it) => {
          const open = openId === it.id;
          const correctIdx = (it.options || []).findIndex(o => o.score === 1);
          return (
            <div key={it.id} className="bg-white border border-kc rounded-[var(--kc-radius-md)]" data-testid={`gm-scenario-${it.id}`}>
              <button onClick={() => setOpenId(open ? null : it.id)} className="w-full flex items-center justify-between p-3 text-left hover:bg-surface-2 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  {open ? <ChevronDown size={16} className="flex-shrink-0 text-muted-kc"/> : <ChevronRight size={16} className="flex-shrink-0 text-muted-kc"/>}
                  <div className="min-w-0">
                    <div className="text-xs text-muted-kc uppercase tracking-wider">{it.id}</div>
                    <div className="font-semibold text-ink truncate">{it.trigger || <span className="italic text-muted-kc">(no trigger)</span>}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[10px] uppercase tracking-wider text-muted-kc">{(it.options || []).length} opts</span>
                  {correctIdx < 0 && <span className="text-[10px] uppercase tracking-wider text-[color:var(--kc-danger)] font-semibold">no correct</span>}
                </div>
              </button>
              {open && (
                <div className="p-4 border-t border-kc space-y-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Trigger (the real-world moment)</label>
                    <input type="text" value={it.trigger} onChange={(e) => updateItem(it.id, { trigger: e.target.value })} className="w-full h-10 px-3 mt-1 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-sm" data-testid={`gm-trigger-${it.id}`}/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Negative thought (the mind's first response)</label>
                    <input type="text" value={it.thought} onChange={(e) => updateItem(it.id, { thought: e.target.value })} className="w-full h-10 px-3 mt-1 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-sm" data-testid={`gm-thought-${it.id}`}/>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Response options (mark exactly one as the strong reframe)</label>
                    <div className="space-y-2 mt-1">
                      {(it.options || []).map((o, i) => (
                        <div key={i} className={`p-3 rounded-[var(--kc-radius-sm)] border-2 ${o.score === 1 ? 'border-[color:var(--kc-gold)] bg-gold-soft' : 'border-kc bg-white'}`} data-testid={`gm-opt-${it.id}-${i}`}>
                          <div className="flex items-start gap-2">
                            <button onClick={() => markCorrect(it.id, i)} title={o.score === 1 ? 'This is the strong reframe' : 'Mark as the strong reframe'} className={`mt-1 w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center ${o.score === 1 ? 'bg-[color:var(--kc-gold)] text-black' : 'bg-surface-2 text-muted-kc hover:text-ink'}`}>
                              <Sparkles size={12}/>
                            </button>
                            <div className="flex-1 space-y-1.5">
                              <input type="text" value={o.text} onChange={(e) => updateOpt(it.id, i, { text: e.target.value })} placeholder="Response text" className="w-full h-9 px-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-sm" data-testid={`gm-opt-text-${it.id}-${i}`}/>
                              <textarea value={o.feedback} onChange={(e) => updateOpt(it.id, i, { feedback: e.target.value })} placeholder="Mick's feedback for this pick…" className="w-full px-3 py-2 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-xs leading-relaxed" rows={2} data-testid={`gm-opt-fb-${it.id}-${i}`}/>
                            </div>
                            <button onClick={() => removeOpt(it.id, i)} className="mt-1 p-1.5 rounded-full text-muted-kc hover:text-[color:var(--kc-danger)] hover:bg-red-50"><Trash2 size={14}/></button>
                          </div>
                        </div>
                      ))}
                      <button onClick={() => addOpt(it.id)} className="h-9 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] border border-dashed border-kc text-xs text-muted-kc hover:bg-surface-2"><Plus size={12}/> Add option</button>
                    </div>
                  </div>
                  <div className="pt-2 flex justify-end">
                    <button onClick={() => deleteItem(it.id)} className="h-9 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs text-[color:var(--kc-danger)] hover:bg-red-50"><Trash2 size={12}/> Delete scenario</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-between flex-wrap gap-3 pt-2">
        <div className="flex gap-2">
          <button onClick={addNew} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] border border-kc-strong bg-white text-sm font-semibold hover:bg-surface-2" data-testid="gm-scenarios-add"><Plus size={14}/> Add scenario</button>
          <button onClick={reset} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm text-muted-kc hover:bg-surface-2 border border-kc"><RotateCcw size={14}/> Reset</button>
        </div>
        <button onClick={save} disabled={saving} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold disabled:opacity-50" data-testid="gm-scenarios-save">
          {saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>} Save scenarios
        </button>
      </div>
    </div>
  );
}

/* =================== XP REWARDS =================== */
function XpRewardsPanel({ cfg, onSaved }) {
  const [r, setR] = useState(cfg.xp_rewards || {});
  const [saving, setSaving] = useState(false);

  const fields = [
    { key: 'journal',                  label: 'Journal entry',           desc: 'XP awarded per journal entry (capped to 1/day for streak).' },
    { key: 'lesson',                   label: 'Lesson complete',         desc: 'XP awarded the first time a lesson is finished.' },
    { key: 'streak_7',                 label: '7-day streak bonus',      desc: 'Extra XP awarded when a user hits a 7-day streak.' },
    { key: 'streak_30',                label: '30-day streak bonus',     desc: 'Extra XP awarded when a user hits a 30-day streak.' },
    { key: 'game_thought_per_point',   label: 'Thought Defense / point', desc: 'XP per point scored in a Thought Defense round. Example: 5 means a score of 10 → 50 XP.' },
    { key: 'game_reframe_per_correct', label: 'Reframe Battle / correct',desc: 'XP per correct reframe in a Reframe Battle round. Example: 20 means 4/5 correct → 80 XP.' },
    { key: 'game_breath_per_minute',   label: 'Breath Rhythm / minute',  desc: 'XP per completed minute of breathing. Example: 10 means a 5-min session → 50 XP.' },
  ];

  const save = async () => {
    setSaving(true);
    try {
      const resp = await api.put('/admin/games/xp-rewards', r);
      setR(resp.data.rewards || r);
      toast.success('XP rewards updated.');
      onSaved();
    } catch (e) {
      toast.error('Save failed.', { description: String(e?.response?.data?.detail || e.message) });
    } finally {
      setSaving(false);
    }
  };
  const reset = async () => {
    if (!window.confirm('Reset XP rewards to defaults (5 / 25 / 50 / 200)?')) return;
    try {
      await api.post('/admin/games/reset-defaults', { sections: ['xp_rewards'] });
      toast.success('Reset to defaults.');
      const resp = await api.get('/admin/games/config');
      setR(resp.data.xp_rewards || {});
      onSaved();
    } catch (e) {
      toast.error('Reset failed.');
    }
  };

  return (
    <div className="space-y-4" data-testid="gm-xp">
      <p className="text-sm text-muted-kc">Tune how much XP each activity awards. XP for games is computed server-side from raw stats × your multipliers below — you control it all. Level threshold: {cfg.xp_per_level} XP per level.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {fields.map(f => (
          <div key={f.key} className="p-4 bg-white border border-kc rounded-[var(--kc-radius-md)]" data-testid={`gm-xp-${f.key}`}>
            <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">{f.label}</label>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="2000"
                value={r[f.key] ?? 0}
                onChange={(e) => setR({ ...r, [f.key]: Math.max(0, Math.min(2000, parseInt(e.target.value) || 0)) })}
                className="w-24 h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white font-display text-xl text-[color:var(--kc-gold-2)] tabular-nums"
                data-testid={`gm-xp-input-${f.key}`}
              />
              <span className="text-sm text-muted-kc">XP</span>
            </div>
            <div className="text-xs text-muted-kc mt-2">{f.desc}</div>
          </div>
        ))}
      </div>
      <div className="flex justify-between gap-3 pt-2">
        <button onClick={reset} className="h-10 px-4 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] text-sm text-muted-kc hover:bg-surface-2 border border-kc"><RotateCcw size={14}/> Reset to defaults</button>
        <button onClick={save} disabled={saving} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold disabled:opacity-50" data-testid="gm-xp-save">
          {saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>} Save XP rewards
        </button>
      </div>
    </div>
  );
}
